import { useEffect, useMemo } from 'react';
import {
  AttributionControl,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';

interface Props {
  position: [number, number] | null;
  onSelect: (latlng: [number, number]) => void;
  /** 推定が出ていれば、その価格を地図上のピンに載せる。 */
  priceLabel?: string | null;
  /** 推定中はピンを控えめにして、結果待ちであることを地図側でも示す。 */
  loading?: boolean;
}

/** Escapes text going into the marker's raw HTML. */
const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/**
 * The dot that sits exactly on the chosen coordinate. Kept beneath whatever
 * label is drawn above it so the point itself is never guessed at.
 */
const DOT = `<span style="position:absolute;left:50%;top:50%;width:14px;height:14px;margin:-7px 0 0 -7px;
  border-radius:50%;background:var(--mk-accent);border:3px solid var(--mk-sheet);
  box-shadow:var(--mk-shadow)"></span>`;

/**
 * Before a price exists the marker is just the dot with a soft halo; once the
 * estimate lands the price rides on the map in a bubble, which is what makes
 * the map the answer rather than a picker sitting next to the answer.
 */
function markerIcon(priceLabel: string | null | undefined, loading: boolean) {
  const bubble =
    priceLabel && !loading
      ? `<span class="map-pin" style="position:absolute;left:50%;top:50%">${esc(priceLabel)}</span>`
      : loading
        ? `<span class="map-pin" style="position:absolute;left:50%;top:50%;border-color:var(--mk-rule-strong);color:var(--mk-ink-3)">推定中</span>`
        : '';

  return L.divIcon({
    className: '',
    html: `<span style="position:relative;display:block;width:0;height:0">
      <span style="position:absolute;left:50%;top:50%;width:34px;height:34px;margin:-17px 0 0 -17px;
        border-radius:50%;background:var(--mk-accent);opacity:0.16"></span>
      ${DOT}${bubble}
    </span>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

const TOKYO_CENTER: [number, number] = [35.6812, 139.7671];

function ClickHandler({ onSelect }: { onSelect: Props['onSelect'] }) {
  useMapEvents({
    click(e) {
      onSelect([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function FlyToOnSelect({ position }: { position: Props['position'] }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.panTo(position);
  }, [map, position]);
  return null;
}

export default function MapPicker({ position, onSelect, priceLabel, loading = false }: Props) {
  const icon = useMemo(() => markerIcon(priceLabel, loading), [priceLabel, loading]);

  return (
    <MapContainer
      center={TOKYO_CENTER}
      zoom={12}
      className="h-full w-full"
      scrollWheelZoom
      attributionControl={false}
    >
      {/* prefix={false} drops Leaflet's own "Leaflet ⚑" badge. The 国土地理院
          credit the tile terms require is set on the TileLayer below and is
          unaffected. */}
      <AttributionControl position="bottomright" prefix={false} />
      <TileLayer
        url="https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank" rel="noreferrer">国土地理院</a>'
        maxZoom={18}
      />
      <ClickHandler onSelect={onSelect} />
      {position && <Marker position={position} icon={icon} />}
      <FlyToOnSelect position={position} />
    </MapContainer>
  );
}
